import React, { useEffect, useState, useRef } from 'react';
import styles from './detailsBox.module.scss';
import policeManIcon from '../../../assets/Imagess/policeMan.svg';
import ApplicantsTab from '../../Atoms/Tabs/InfoTab';
import emailIcon from '../../../assets/Imagess/GreenEmail.svg';
import phoneIcon from '../../../assets/Imagess/GreenPhone.svg';
import AttachmentsIcon from '../../../assets/Imagess/AttachmentsIcon.svg';
import BackIcon from '../../../assets/Imagess/BackIcon.svg';
import { useDownloadAllAttachments } from '../../../Hooks/Applications';
import ReseachFile from '../../Atoms/Cards/ResearchFile';
import Loader from '../Loader/Loader';
import moment from 'moment';
import {
  KIND_STUDY,
  researchFullName,
  researchEntryTitle,
  researchAffiliation,
  researchFieldLabel,
  researchAttachments,
  submissionKindLabel,
  identifierUrl,
  RESEARCH_EMPTY_VALUE,
} from '../../../app/helpers/researchEntry';
import {
  RESEARCH_STATUS,
  RESEARCH_STATUS_LABELS,
} from '../../../app/helpers/researchQuery';

function DetailsBox({
  data,
  /** §A1 — set by the page; absent until the status endpoint ships. */
  onStatusChange,
  /** Surfaces a message when an attachment cannot be fetched. */
  onAttachmentError,
  mode,
  setDetails,
  refetchSingleResearch,
  setMoveRight,
  researchId,
  isLoading,
  researchDetails,
  isLoadingDetails,
}) {
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState('documents');
  let componentRef = useRef();

  const tabs = [
    {
      index: 0,
      name: 'documents',
      text: 'الوثائق',
      icon: AttachmentsIcon,
    },
  ];

  useEffect(() => {
    if (data && data[0]) {
      const entry = data[0];
      // `researchFullName` reads `fullName` on a registry record and falls back
      // to the old first/middle/last columns, so entries created before the
      // October 2026 change still show a name instead of "undefined undefined".
      setInfo({
        // Base64 on pre-registry records; new ones carry no content at all,
        // so the photo comes from the blob origin when there is one.
        photo:
          entry.profilePic_Content ||
          researchAttachments(entry).find((f) => f.key === 'profilePic')?.url ||
          '',
        fullName: researchFullName(entry),
        email: entry.email,
        phone: entry.mobile,
        /* `researchTypeDisplay` was read here and is returned by NO
           endpoint, so «مجال البحث» rendered as nothing at all. The real
           field is `researchType`, and since 2026-11-03 it holds the
           researcher's own words rather than a sector id — the helper reads
           both shapes. */
        program: researchFieldLabel(entry),
        Files: [],
      });
    }
  }, [data]);

  const entry = data?.[0];
  const isStudy = entry?.submissionKind === KIND_STUDY;
  /* The record now carries its own file fields and a `blobUrl`; the separate
     documents call is the older source, and is merged under it for records
     that predate the change. */
  const attachments = researchAttachments(entry, researchDetails?.[0]);

  /**
   * A fresh, signed address for one attachment, fetched at the moment of the
   * click.
   *
   * The documents moved to private storage on 2026-10-19 and the signed URL
   * is valid for FIFTEEN MINUTES. This screen fetches it when it opens, which
   * is not when the operator clicks download — so the record is re-read first
   * and the url taken from that answer. If the refetch gives us nothing
   * usable, the card falls back to the address it already has, which may
   * still be inside the window.
   */
  const resolveAttachmentUrl = async (key) => {
    const fresh = await refetchSingleResearch?.();
    const record =
      fresh?.data?.data?.[0] ?? fresh?.data?.[0] ?? undefined;
    if (!record) return '';
    return researchAttachments(record).find((f) => f.key === key)?.url || '';
  };
  const doiHref = identifierUrl('doi', entry?.doi);
  const orcidHref = identifierUrl('orcid', entry?.orcidId);

  /**
   * One label/value row, rendered only when there is a value to show.
   *
   * The `researchEntry` helpers return the dash sentinel rather than an empty
   * string, because a TABLE cell has to be filled. A detail row does not — an
   * empty one is just noise — so the sentinel counts as absent here.
   */
  /**
   * `ltr` is for identifiers, and it is not cosmetic.
   *
   * A DOI is `10.1000/xyz123` — digits, a SLASH, then letters. The slash is
   * bidi-neutral, so in an Arabic paragraph the algorithm resolves the two
   * runs right-to-left and renders it «xyz123/10.1000». It looks like a
   * styling quirk and is actually a wrong identifier: an operator who copies
   * what they see gets something that resolves to nothing.
   *
   * `dir="ltr"` on the value pins the run. The label beside it stays Arabic.
   */
  const Fact = ({ label, value, href, wide, ltr }) =>
    value && value !== RESEARCH_EMPTY_VALUE ? (
      <div className={`${styles.fact} ${wide ? styles.factWide : ''}`}>
        <dt className={styles.factLabel}>{label}</dt>
        <dd className={styles.factValue} dir={ltr ? 'ltr' : undefined}>
          {href ? (
            // Operators click through to the published record; a bare
            // identifier is not a link.
            <a href={href} target='_blank' rel='noopener noreferrer'>
              {value}
            </a>
          ) : (
            value
          )}
        </dd>
      </div>
    ) : null;

  const downloadAttachment = useDownloadAllAttachments(researchId);

  const handleDownloadAll = async (contentType) => {
    try {
      var data = await downloadAttachment.mutateAsync();

      if (data.data instanceof Blob) {
        const blob = new Blob([data.data], {
          type: contentType,
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'جميع الملفات المرفقة';
        link.click();

        window.URL.revokeObjectURL(link.href);
      } else {
        console.error('نوع البيانات غير متوقع. كان من المفترض أن يكون سلسلة.');
      }
    } catch (error) {
      console.error('خطأ في تحميل الملف:', error);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div
        className={`${styles['parentContainer']}`}
        id={'PrintDetails'}
        ref={(el) => (componentRef = el)}
      >
        <div className={styles.recordHeader}>
          <div className={styles.identity}>
            <div className={styles.avatar}>
              <img src={policeManIcon} alt='' />
            </div>
            <div className={styles.identityText}>
              {info ? <h3 className={styles.name}>{info.fullName}</h3> : null}
              <div className={styles.contact}>
                {info?.email ? (
                  <a href={`mailto:${info.email}`} className={styles.contactItem}>
                    <img src={emailIcon} alt='' />
                    {info.email}
                  </a>
                ) : null}
                {info?.phone ? (
                  <a href={`tel:${info.phone}`} className={styles.contactItem}>
                    <img src={phoneIcon} alt='' />
                    <span dir='ltr'>{info.phone}</span>
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          {/* The one ACTION on this screen, so it sits apart from the read-only
              facts rather than being the eighth row of a list. An operator
              comes here to deal with a submission; archiving it is the act of
              having dealt with it. */}
          {entry ? (
            <div className={styles.statusBox}>
              <span className={styles.statusLabel}>الحالة</span>
              <div className={styles.statusControl}>
                <span
                  className={`${styles.statusDot} ${
                    styles[`dot_${String(entry.status || 'new').toLowerCase()}`] || ''
                  }`}
                  aria-hidden='true'
                />
                <select
                  value={String(entry.status || RESEARCH_STATUS.NEW)}
                  onChange={(e) => onStatusChange?.(e.target.value)}
                  aria-label='حالة الطلب'
                  className={`${styles.statusSelect} ${styles['no-print']}`}
                >
                  {Object.entries(RESEARCH_STATUS_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <span className='print-only'>
                  {RESEARCH_STATUS_LABELS[
                    String(entry.status || '').toLowerCase()
                  ] || '-'}
                </span>
              </div>

              {/* The DATE only. `statusUpdatedBy` is a raw user GUID — it was
                  being printed to the screen, which tells an operator nothing
                  and looks like a defect. It is shown only when the API also
                  gives a name to go with it. */}
              {entry.statusUpdatedOn ? (
                <span className={styles.statusMeta}>
                  {moment(entry.statusUpdatedOn).format('YYYY/MM/DD — HH:mm')}
                  {entry.statusUpdatedByName
                    ? ` · ${entry.statusUpdatedByName}`
                    : ''}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* A GRID, not a stack. Stacked, every label and value took a full row
            of a very wide card and left the other two thirds empty — the same
            lopsided block the October review objected to on the website. */}
        <dl className={styles.facts}>
          <Fact label='مجال البحث' value={info?.program} />
          <Fact label='صفة مقدم الطلب' value={researchAffiliation(entry)} />

          {/* FB-R2 — «في حالة كان من موظفي الشرطة يرجى إدراج الرقم الوظيفي +
              الرتبة». Beside «صفة مقدم الطلب», because that is the row that
              already answers "who is this person". `Fact` renders nothing for
              an empty value, so an entry submitted before these existed shows
              no blank rows. The number is `ltr` — it is digits, and a bidi
              run can reorder them. */}
          <Fact label='الرقم الوظيفي' value={entry?.employeeNumber} ltr />
          <Fact label='الرتبة' value={entry?.rank} />

          <Fact
            label='نوع التسجيل'
            value={submissionKindLabel(entry?.submissionKind)}
          />
          <Fact
            label={isStudy ? 'عنوان الدراسة' : 'عنوان البحث'}
            value={researchEntryTitle(entry)}
            wide
          />

          {/* FB-R3 / FB-R4 — these two are no longer branch-specific.
              The FIELD is the same on both sides; only the label changes,
              which is the whole reason the website sends one field rather
              than asking the backend for a duplicate. See the note at the top
              of DPA-FE's factory/Data.jsx. */}
          <Fact
            label={isStudy ? 'سنة إعداد الدراسة' : 'سنة النشر'}
            value={entry?.publicationYear}
          />
          <Fact
            label={isStudy ? 'المشاركون في الدراسة' : 'أسماء الباحثين'}
            value={entry?.studyParticipants}
            wide
          />

          {isStudy ? (
            /* FB-R4 — «نبذة عن الدراسة». Full width: it is a paragraph, and
               a paragraph in a third of a card is a column of single words. */
            <Fact
              label='نبذة عن الدراسة'
              value={entry?.studyAbstract}
              wide
            />
          ) : (
            <>
              <Fact label='اسم المجلة العلمية' value={entry?.journalName} />
              <Fact label='DOI' value={entry?.doi} href={doiHref} ltr />
              <Fact
                label='ORCID iD'
                value={entry?.orcidId}
                href={orcidHref}
                ltr
              />
              {/* FB-R3 — «رابط للبحث المنشور إن وجد». The website gives every
                  stored link a scheme (see normaliseUrl in Factory.jsx), so
                  this href is absolute and cannot resolve against the CMS's
                  own origin. `ltr` for the same reason as DOI: a URL in a
                  bidi-neutral context renders its segments backwards. */}
              <Fact
                label='رابط البحث المنشور'
                value={entry?.publishedResearchUrl}
                href={entry?.publishedResearchUrl}
                ltr
                wide
              />
            </>
          )}
        </dl>

        <div
          className={`${styles['tab-select-container']} ${styles['row']}`}
          style={{ marginTop: '25px', marginBottom: '15px' }}
        >
          <div className={`${styles['tab-container']} ${styles['w-90']}`}>
            {tabs.map((tab) => (
              <ApplicantsTab
                key={tab.index}
                text={tab.text}
                icon={tab.icon}
                handleOnClick={() => {
                  setSelected(tab.name);
                }}
                active={tab.name === selected ? true : false}
                shadowed={false}
              />
            ))}
          </div>

          <div
            style={{ position: 'absolute', right: '40px', marginTop: '0px' }}
            className={`${styles['no-print']}`}
          ></div>
        </div>

        {isLoadingDetails ? (
          <Loader />
        ) : (
          /* The four uploads became ONE. A registry entry carries the research
             or the study and nothing else; the photograph, CV and English
             abstract belonged to journal submission and are no longer
             collected. They are still RENDERED when present, because the 14
             records created before the change still have them — but each card
             is now conditional, so a new entry shows one card rather than one
             card and three empty ones. */
          <div className={styles.attachments}>
            {attachments.length ? (
              attachments.map((file) => (
                <ReseachFile
                  key={file.key}
                  fileName={file.fileName}
                  type={file.label}
                  url={file.url}
                  /* Only the URL-backed files need re-signing; a legacy
                     base64 record has nothing to expire. */
                  resolveUrl={
                    file.url ? () => resolveAttachmentUrl(file.key) : undefined
                  }
                  onError={onAttachmentError}
                  base64String={file.base64}
                  mimeType={file.mimeType}
                />
              ))
            ) : (
              <p className={styles.attachmentsEmpty}>
                لا توجد مرفقات لهذا السجل
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default DetailsBox;
